'use client';

import { useMemo } from 'react';
import { useScrollReveal } from './useScrollReveal';
import type { CalendarEvent } from '@/lib/types';
import { getTypeLabel, getEventColor } from '@/lib/constants';
import { formatMonthShort, formatDayShort } from '@/lib/date-format';
import { durationDaysInclusive } from '@/lib/date-calc';

function formatDateRange(dateStart: string, dateEnd: string, lang: string): { label: string; days: number } {
  const s = new Date(dateStart + 'T00:00:00');
  const e = new Date(dateEnd + 'T00:00:00');
  const days = durationDaysInclusive(dateStart, dateEnd);
  const startLabel = `${s.getDate()} ${formatDayShort(dateStart, lang)}`;
  if (days <= 1) return { label: startLabel, days: 1 };
  const endLabel = `${e.getDate()} ${formatDayShort(dateEnd, lang)}`;
  return { label: `${startLabel}–${endLabel}`, days };
}

interface MonthSectionProps {
  monthIndex: number;
  year: number;
  events: CalendarEvent[];
  lang: string;
}

function MonthSection({ monthIndex, year, events, lang }: MonthSectionProps) {
  const { ref, revealed } = useScrollReveal();
  const monthName = formatMonthShort(monthIndex, lang).toUpperCase();

  return (
    <div ref={ref} className={`scroll-reveal ${revealed ? 'revealed' : ''}`}>
      {/* Month header */}
      <div className="flex items-center justify-between border-b border-border-subtle bg-surface-hover/50 px-4 py-2">
        <span className="text-xs font-bold uppercase tracking-wider text-text-primary">
          {monthName} {year}
        </span>
        <span className="text-xs tabular-nums text-muted">
          {events.length} event{events.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Event rows */}
      {events.length === 0 ? (
        <div className="px-4 py-3 text-sm italic text-muted">No events</div>
      ) : (
        <div className="divide-y divide-border-subtle">
          {events.map((evt) => {
            const { label, days } = formatDateRange(evt.dateStart, evt.dateEnd, lang);
            return (
              <div
                key={evt.id}
                className="flex items-center gap-3 px-4 py-2 text-sm"
              >
                {/* Date */}
                <span className="w-24 shrink-0 tabular-nums text-muted">
                  {label}
                </span>

                {/* Color dot */}
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: getEventColor(evt.type) }}
                />

                {/* Title */}
                <span className="min-w-0 flex-1 truncate text-text-primary">
                  {evt.title}
                </span>

                {/* Type label — hidden on mobile */}
                <span className="hidden shrink-0 text-xs text-muted sm:inline">
                  {getTypeLabel(evt.type)}
                </span>

                {/* Duration badge — multi-day only, hidden on mobile */}
                {days > 1 && (
                  <span className="hidden shrink-0 rounded bg-surface-hover px-1.5 py-0.5 text-caption tabular-nums text-muted sm:inline">
                    {days}d
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface LedgerShowcaseTimelineProps {
  events: CalendarEvent[];
  year: number;
  lang?: string;
  /** Academic pages span two calendar years — render only months with events,
   *  in true chronological order, each labeled with its own year. */
  lens?: string;
}

export function LedgerShowcaseTimeline({ events, year, lang = 'en', lens }: LedgerShowcaseTimelineProps) {
  // Academic: group by calendar year+month ("YYYY-MM") and render only months
  // that have events, in chronological order — so an academic year spanning two
  // calendar years reads Aug 2026 → Jul 2027 with correct per-month year labels.
  const academicMonths = useMemo(() => {
    if (lens !== 'academic') return null;
    const byKey = new Map<string, CalendarEvent[]>();
    for (const evt of events) {
      const key = evt.dateStart.slice(0, 7); // YYYY-MM
      const arr = byKey.get(key) ?? [];
      arr.push(evt);
      byKey.set(key, arr);
    }
    return [...byKey.keys()].sort().map((key) => {
      const [y, m] = key.split('-');
      return {
        key,
        year: parseInt(y, 10),
        monthIndex: parseInt(m, 10) - 1,
        events: byKey.get(key)!.sort((a, b) => a.dateStart.localeCompare(b.dateStart)),
      };
    });
  }, [events, lens]);

  const eventsByMonth = useMemo(() => {
    const grouped: CalendarEvent[][] = Array.from({ length: 12 }, () => []);
    for (const evt of events) {
      const month = new Date(evt.dateStart + 'T00:00:00').getMonth();
      grouped[month].push(evt);
    }
    for (const group of grouped) {
      group.sort((a, b) => a.dateStart.localeCompare(b.dateStart));
    }
    return grouped;
  }, [events]);

  return (
    <div className="overflow-hidden content-card">
      {academicMonths
        ? academicMonths.map((mo) => (
            <MonthSection key={mo.key} monthIndex={mo.monthIndex} year={mo.year} events={mo.events} lang={lang} />
          ))
        : Array.from({ length: 12 }, (_, i) => (
            <MonthSection key={i} monthIndex={i} year={year} events={eventsByMonth[i]} lang={lang} />
          ))}
    </div>
  );
}
